import { useState, useEffect } from 'react'
import { X, Search, ArrowRightLeft } from 'lucide-react'
import api from '../core/services/api'

interface UserOption {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
}

interface TransferOwnershipModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: 'offerta' | 'commessa'
  entityId: string
  currentOwnerId: string | null
  onTransferred: () => void
}

export function TransferOwnershipModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  currentOwnerId,
  onTransferred,
}: TransferOwnershipModalProps) {
  const [users, setUsers] = useState<UserOption[]>([])
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      loadUsers()
      setSearch('')
      setSelectedId(null)
      setError('')
    }
  }, [isOpen])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const res = await api.get('/v1/anagrafiche/utenti')
      setUsers(res.data.filter((u: UserOption) => u.id !== currentOwnerId))
    } catch {
      setError('Errore nel caricamento utenti')
    } finally {
      setLoading(false)
    }
  }

  const handleTransfer = async () => {
    if (!selectedId) return
    try {
      setSaving(true)
      setError('')
      const endpoint = entityType === 'offerta'
        ? `/v1/offerte/${entityId}/trasferisci`
        : `/v1/commesse/${entityId}/trasferisci`
      await api.put(endpoint, { new_owner_id: selectedId })
      onTransferred()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore nel trasferimento')
    } finally {
      setSaving(false)
    }
  }

  const filtered = users.filter(u => {
    const q = search.toLowerCase()
    return u.email.toLowerCase().includes(q) ||
      (u.first_name?.toLowerCase() || '').includes(q) ||
      (u.last_name?.toLowerCase() || '').includes(q)
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Cedi Controllo</h2>
            <p className="text-sm text-gray-500">
              Trasferisci la proprietà {entityType === 'offerta' ? "dell'offerta" : 'della commessa'} a un altro utente
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca utente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>

          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
            {loading ? (
              <div className="p-4 text-center text-gray-500 text-sm">Caricamento...</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">Nessun utente trovato</div>
            ) : (
              filtered.map(u => (
                <button
                  key={u.id}
                  onClick={() => setSelectedId(u.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                    selectedId === u.id ? 'bg-primary-50 border-l-2 border-primary-500' : ''
                  }`}
                >
                  <div className="font-medium text-gray-900 text-sm">
                    {u.first_name} {u.last_name}
                  </div>
                  <div className="text-xs text-gray-500">{u.email}</div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
          >
            Annulla
          </button>
          <button
            onClick={handleTransfer}
            disabled={!selectedId || saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
          >
            <ArrowRightLeft className="w-4 h-4" />
            {saving ? 'Trasferimento...' : 'Trasferisci'}
          </button>
        </div>
      </div>
    </div>
  )
}
